/**
 * FluxSupplyBreakdown — V2.K.2
 *
 * Renders a supply breakdown for FLUX on the FluxNodeMonitor page.
 *
 * Why Flux is a different story than HBAR / Cronos :
 *  - Flux is a UTXO chain (Zcash fork) — there is NO pre-mint, NO
 *    big vesting escrow like Cronos CIP-006. New supply enters
 *    circulation via mining block rewards.
 *  - PoUW v2 (mainnet upgrade Oct 2025) ended traditional halvings
 *    and replaced them with a continuous 10 %/year emission decay
 *    targeting < 1 % annual inflation by ~2036.
 *  - The Flux Foundation receives a fixed 0.5 FLUX per block as a
 *    funding stream (continuous, not a locked allocation).
 *
 * So the breakdown surfaces :
 *   - Circulating vs Max supply
 *   - Remaining to mint before the 440 M cap
 *   - The PoUW v2 emission decay mechanic (informational note)
 *   - The Foundation funding stream (0.5 FLUX / block)
 *   - A "known wallets" section restricted to publicly verifiable
 *     entries (block explorer URL — no foundation address has been
 *     officially published in a verifiable source as of writing).
 */

import { useTranslation } from 'react-i18next';
import { useFluxSupply } from '../../hooks/useFluxSupply';

// PoUW v2 parameters — mandatory fork activated at blockheight
// 2 020 000 on 25 October 2025. Block time was shortened from 2 min
// to 30 s, halvings were removed, block reward fixed at 14 FLUX, and
// the emission decays 10 % every 1 051 200 blocks (≈ 1 year of 30 s
// blocks). Max supply was raised from 440 M to 560 M.
const FLUX_BLOCK_REWARD = 14;
// 1 051 200 blocks = 30 s × 1 051 200 ≈ 365 days — the decay window.
const BLOCKS_PER_YEAR = 1_051_200;
const ANNUAL_EMISSION_BASELINE = FLUX_BLOCK_REWARD * BLOCKS_PER_YEAR;
const ANNUAL_DECAY_RATE = 0.10;

// Known Flux addresses / endpoints — only entries verifiable from
// public sources. The Foundation block-reward wallet address is NOT
// listed because no reputable public source has published it in a
// verifiable form.
const KNOWN_ENDPOINTS: Array<{
  label: string;
  url: string;
  category: 'explorer' | 'docs' | 'foundation';
  note: string;
}> = [
  {
    label: 'Flux Block Explorer',
    url: 'https://explorer.runonflux.io/',
    category: 'explorer',
    note: 'Explorateur officiel — adresses, blocs, transactions.',
  },
  {
    label: 'Flux Daemon Indexer (open-source)',
    url: 'https://github.com/RunOnFlux/flux-indexer-explorer',
    category: 'docs',
    note: 'Indexer officiel Flux v9.0.0+ (Proof-of-Node consensus, REST API).',
  },
  {
    label: 'Flux Foundation / dev fund (PoUW v2)',
    url: 'https://runonflux.com/forking-flux-proof-of-useful-work-v2/',
    category: 'foundation',
    note: 'Split block reward PoUW v2 confirmé : 9 Stratus + 3,5 Nimbus + 1 Cumulus + 0,5 Foundation = 14 FLUX/bloc. Adresse de réception du dev fund non publiée — surveiller via l\'explorer officiel.',
  },
];

export function FluxSupplyBreakdown() {
  const { t, i18n } = useTranslation();
  const { data, isLoading, error } = useFluxSupply();

  const locale = i18n?.language || 'fr';
  const fmt = (n: number) =>
    n.toLocaleString(locale === 'fr' ? 'fr-FR' : locale, { maximumFractionDigits: 0 });
  const fmtM = (n: number) =>
    `${(n / 1_000_000).toLocaleString(locale === 'fr' ? 'fr-FR' : locale, {
      maximumFractionDigits: 2,
    })} M`;

  return (
    <section
      aria-labelledby="flux-supply-breakdown-title"
      style={{
        background: 'rgba(0, 0, 0, 0.25)',
        backdropFilter: 'blur(20px)',
        borderRadius: '16px',
        border: '1px solid rgba(255, 255, 255, 0.08)',
        padding: '20px',
        marginBottom: '20px',
      }}
    >
      <header style={{ marginBottom: '14px' }}>
        <h2
          id="flux-supply-breakdown-title"
          style={{
            fontSize: '16px',
            fontWeight: 700,
            margin: 0,
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <span aria-hidden style={{ color: '#2b7be4' }}>◐</span>
          {t('flux.supply.title', 'Supply FLUX — répartition')}
        </h2>
        <div style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '4px' }}>
          {t(
            'flux.supply.subtitle',
            'Ce qui circule, ce qui reste à émettre, et comment fonctionne le PoUW v2 — sources CoinGecko + whitepaper Flux.',
          )}
        </div>
      </header>

      {isLoading ? (
        <SkeletonBlock />
      ) : error || !data ? (
        <ErrorBlock />
      ) : (
        <>
          <StatGrid data={data} fmt={fmt} fmtM={fmtM} t={t} />
          <StackedBar data={data} t={t} />
          <Legend t={t} />
          <EmissionNote
            t={t}
            fmt={fmt}
            annualEmission={ANNUAL_EMISSION_BASELINE}
            decayRate={ANNUAL_DECAY_RATE}
          />
          <KnownEndpoints t={t} />
        </>
      )}
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Stat grid
// ─────────────────────────────────────────────────────────────────────

interface StatGridProps {
  data: NonNullable<ReturnType<typeof useFluxSupply>['data']>;
  fmt: (n: number) => string;
  fmtM: (n: number) => string;
  t: (key: string, fallback: string) => string;
}

function StatGrid({ data, fmt, fmtM, t }: StatGridProps) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        gap: '12px',
        marginBottom: '16px',
      }}
    >
      <StatCard
        label={t('flux.supply.circulating', 'En circulation')}
        value={fmtM(data.circulating)}
        sub={`${fmt(data.circulating)} FLUX`}
        color="#10b981"
      />
      <StatCard
        label={t('flux.supply.total', 'Supply totale')}
        value={fmtM(data.total)}
        sub={`${data.floatPct.toFixed(1)} % en circulation`}
        color="#2b7be4"
      />
      <StatCard
        label={t('flux.supply.remaining', 'Reste à miner')}
        value={fmtM(data.remainingToMint)}
        sub={`${(100 - data.mintedPct).toFixed(1)} % du cap`}
        color="#f59e0b"
      />
      <StatCard
        label={t('flux.supply.max', 'Cap maximal')}
        value={fmtM(data.max)}
        sub={t('flux.supply.maxNote', 'Plafond whitepaper')}
        color="#6366f1"
      />
    </div>
  );
}

function StatCard({
  label,
  value,
  sub,
  color,
}: {
  label: string;
  value: string;
  sub: string;
  color: string;
}) {
  return (
    <div
      style={{
        background: `${color}10`,
        border: `1px solid ${color}30`,
        borderRadius: '10px',
        padding: '12px 14px',
      }}
    >
      <div
        style={{
          fontSize: '11px',
          color: 'var(--muted)',
          textTransform: 'uppercase',
          letterSpacing: '0.4px',
          fontWeight: 600,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: '20px',
          fontWeight: 700,
          color: 'var(--text)',
          marginTop: '4px',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value}
      </div>
      <div
        style={{
          fontSize: '11px',
          color: 'var(--muted)',
          marginTop: '2px',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {sub}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Stacked bar
// ─────────────────────────────────────────────────────────────────────

function StackedBar({
  data,
  t,
}: {
  data: NonNullable<ReturnType<typeof useFluxSupply>['data']>;
  t: (key: string, fallback: string) => string;
}) {
  const circulatingPct = (data.circulating / data.max) * 100;
  // Flux has no big locked allocation — the gap between circulating
  // and total is small (treasury-side reserve, exchange floats, etc.).
  // We surface it but expect it to be tiny.
  const reservedPct = Math.max(0, ((data.total - data.circulating) / data.max) * 100);
  const remainingPct = Math.max(0, 100 - circulatingPct - reservedPct);

  return (
    <div
      style={{ marginBottom: '8px' }}
      aria-label={t(
        'flux.supply.barAlt',
        'Répartition de la supply FLUX sur le cap maximal post-PoUW v2',
      )}
      role="img"
    >
      <div
        style={{
          display: 'flex',
          height: '24px',
          borderRadius: '8px',
          overflow: 'hidden',
          border: '1px solid var(--border)',
        }}
      >
        <div
          style={{
            width: `${circulatingPct}%`,
            background: 'linear-gradient(90deg, #10b981, #34d399)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontSize: '11px',
            fontWeight: 700,
            transition: 'width 0.5s ease',
          }}
          title={`${circulatingPct.toFixed(1)} %`}
        >
          {circulatingPct > 8 ? `${circulatingPct.toFixed(0)} %` : ''}
        </div>
        {reservedPct > 0.5 && (
          <div
            style={{
              width: `${reservedPct}%`,
              background: 'linear-gradient(90deg, #2b7be4, #60a5fa)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontSize: '11px',
              fontWeight: 700,
              transition: 'width 0.5s ease',
            }}
            title={`${reservedPct.toFixed(1)} %`}
          >
            {reservedPct > 4 ? `${reservedPct.toFixed(1)} %` : ''}
          </div>
        )}
        <div
          style={{
            width: `${remainingPct}%`,
            background:
              'repeating-linear-gradient(45deg, rgba(255,255,255,0.05), rgba(255,255,255,0.05) 6px, rgba(255,255,255,0.10) 6px, rgba(255,255,255,0.10) 12px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--muted)',
            fontSize: '10px',
            fontWeight: 600,
            transition: 'width 0.5s ease',
          }}
          title={`${remainingPct.toFixed(1)} %`}
        >
          {remainingPct > 8 ? `${remainingPct.toFixed(1)} %` : ''}
        </div>
      </div>
    </div>
  );
}

function Legend({ t }: { t: (key: string, fallback: string) => string }) {
  return (
    <div
      style={{
        display: 'flex',
        gap: '14px',
        flexWrap: 'wrap',
        fontSize: '11px',
        color: 'var(--muted)',
        marginBottom: '14px',
      }}
    >
      <LegendItem color="#10b981" label={t('flux.supply.legendCirc', 'En circulation (free float)')} />
      <LegendItem color="#2b7be4" label={t('flux.supply.legendReserved', 'Différentiel total / circulating')} />
      <LegendItem
        color="rgba(255,255,255,0.15)"
        label={t('flux.supply.legendToMint', 'Reste à émettre avant le cap')}
      />
    </div>
  );
}

function LegendItem({ color, label }: { color: string; label: string }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
      <span
        aria-hidden
        style={{
          width: '10px',
          height: '10px',
          borderRadius: '3px',
          background: color,
          display: 'inline-block',
        }}
      />
      {label}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Emission note (PoUW v2)
// ─────────────────────────────────────────────────────────────────────

function EmissionNote({
  t,
  fmt,
  annualEmission,
  decayRate,
}: {
  t: (key: string, fallback: string) => string;
  fmt: (n: number) => string;
  annualEmission: number;
  decayRate: number;
}) {
  const body = t(
    'flux.supply.emissionBody',
    "Depuis le passage à Proof-of-Useful-Work v2 (fork mandatoire activé le 25 octobre 2025 au blockheight 2 020 000), les halvings traditionnels ont été supprimés. Le block reward est fixé à {{reward}} FLUX et le temps de bloc est descendu de 2 min à 30 s. L'émission décroît de {{decay}} % tous les 1 051 200 blocs (~1 an), pour viser moins de 1 % d'inflation annuelle d'ici ~2036. Émission annuelle de base : ~{{annual}} FLUX (avant décroissance), répartie ainsi par bloc : 9 FLUX Stratus, 3,5 FLUX Nimbus, 1 FLUX Cumulus, 0,5 FLUX Flux Foundation.",
  )
    .replace('{{reward}}', String(FLUX_BLOCK_REWARD))
    .replace('{{decay}}', String(Math.round(decayRate * 100)))
    .replace('{{annual}}', fmt(annualEmission));

  return (
    <div
      style={{
        padding: '12px 14px',
        background: 'rgba(43, 123, 228, 0.06)',
        border: '1px dashed rgba(43, 123, 228, 0.35)',
        borderRadius: '10px',
        fontSize: '12px',
        color: 'var(--text)',
        lineHeight: 1.6,
        marginBottom: '14px',
      }}
    >
      <strong style={{ color: '#2b7be4' }}>
        {t('flux.supply.emissionTitle', 'Émission — PoUW v2 (actif depuis 25 octobre 2025)')}
      </strong>
      <div style={{ marginTop: '4px' }}>{body}</div>
      <div style={{ fontSize: '11px', color: 'var(--muted)', marginTop: '6px' }}>
        {t(
          'flux.supply.emissionSource',
          'Sources : whitepaper Flux, fork Daemon v8.0.0 (juillet 2025), explorer.runonflux.io.',
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Known endpoints / addresses
// ─────────────────────────────────────────────────────────────────────

function KnownEndpoints({ t }: { t: (key: string, fallback: string) => string }) {
  return (
    <details
      style={{
        background: 'rgba(255, 255, 255, 0.02)',
        border: '1px solid var(--border)',
        borderRadius: '10px',
        padding: '12px 14px',
      }}
    >
      <summary
        style={{
          fontSize: '13px',
          fontWeight: 600,
          color: 'var(--text)',
          cursor: 'pointer',
          listStyle: 'revert',
        }}
      >
        {t('flux.supply.knownEndpointsTitle', 'Sources officielles & wallets connus')}
      </summary>

      <div style={{ fontSize: '11px', color: 'var(--muted)', marginTop: '8px', lineHeight: 1.6 }}>
        {t(
          'flux.supply.knownEndpointsDisclaimer',
          'Flux est une chaîne UTXO (fork Zcash) — pas d\'adresse de burn standard ni de gros pre-mint verrouillé. L\'adresse exacte du wallet Flux Foundation qui reçoit le 0,5 FLUX / bloc n\'a pas été publiée dans une source publique vérifiable, donc non listée pour éviter toute désinformation.',
        )}
      </div>

      <ul style={{ listStyle: 'none', padding: 0, margin: '10px 0 0' }}>
        {KNOWN_ENDPOINTS.map((entry) => (
          <li
            key={entry.url}
            style={{
              padding: '10px 0',
              borderTop: '1px solid var(--border)',
              fontSize: '12px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', flexWrap: 'wrap' }}>
              <CategoryPill category={entry.category} />
              <a
                href={entry.url}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: 'var(--text)', fontWeight: 600, textDecoration: 'none' }}
              >
                {entry.label} ↗
              </a>
            </div>
            <div style={{ fontSize: '11px', color: 'var(--muted)', marginTop: '4px' }}>
              {entry.note}
            </div>
          </li>
        ))}
      </ul>
    </details>
  );
}

function CategoryPill({ category }: { category: 'explorer' | 'docs' | 'foundation' }) {
  const styles: Record<typeof category, { bg: string; color: string; label: string }> = {
    explorer: { bg: 'rgba(43, 123, 228, 0.15)', color: '#60a5fa', label: 'Explorer' },
    docs: { bg: 'rgba(99, 102, 241, 0.15)', color: '#818cf8', label: 'Docs / API' },
    foundation: { bg: 'rgba(245, 158, 11, 0.15)', color: '#fbbf24', label: 'Foundation' },
  };
  const s = styles[category];
  return (
    <span
      style={{
        fontSize: '10px',
        fontWeight: 700,
        padding: '2px 6px',
        borderRadius: '4px',
        background: s.bg,
        color: s.color,
        textTransform: 'uppercase',
        letterSpacing: '0.3px',
      }}
    >
      {s.label}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Loading / error states
// ─────────────────────────────────────────────────────────────────────

function SkeletonBlock() {
  return (
    <div aria-hidden>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: '12px',
          marginBottom: '16px',
        }}
      >
        {[0, 1, 2, 3].map((i) => (
          <div
            key={i}
            style={{
              background: 'rgba(255, 255, 255, 0.04)',
              borderRadius: '10px',
              height: '72px',
            }}
          />
        ))}
      </div>
      <div
        style={{
          height: '24px',
          background: 'rgba(255, 255, 255, 0.04)',
          borderRadius: '8px',
          marginBottom: '14px',
        }}
      />
    </div>
  );
}

function ErrorBlock() {
  return (
    <div
      style={{
        padding: '14px',
        background: 'rgba(239, 68, 68, 0.08)',
        border: '1px solid rgba(239, 68, 68, 0.25)',
        borderRadius: '10px',
        fontSize: '12px',
        color: 'var(--muted)',
      }}
    >
      Données CoinGecko indisponibles pour le moment. La supply FLUX sera affichée dès que l'API répondra.
    </div>
  );
}
